import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import printJS from 'print-js';
import './printQuotePage.css';
import { NODE_BACKEND_API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import QRCode from 'qrcode';
import {
    getClientConditionLabel,
    shouldShowFinalPrice,
    calcIvaIncluido,
    getDiscountConIva,
} from '../utils/invoiceDisplay';

// Helper function to calculate the final retail price to print.
//
// Hay dos modos:
//  - brandScopedRounding (GIULIANO): el backend ya devuelve el precio de
//    presentacion final (redondeado por marca). Se imprime tal cual; redondear
//    aca extenderia el redondeo a marcas que no lo piden.
//  - legacy (CIA y demas): redondeo uniforme al step 100, con DUTTO_TEST exento.
const calculateRoundedRetailPrice = (originalPrice, clientDatabaseName, brandScopedRounding = false) => {
    const price = parseFloat(originalPrice);
    if (isNaN(price)) {
        return 0;
    }

    if (brandScopedRounding || clientDatabaseName === "DUTTO_TEST") {
        return price;
    }
    return Math.ceil(price / 100) * 100;
};

// Helper function to format price for display
const formatPrice = (price) => {
    if (typeof price !== 'number' || isNaN(price)) {
        return 'N/A';
    }
    // Show 2 decimal places for AFIP compliance (credit notes require exact amounts)
    return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Helper to categorize credit note
const getCreditNoteCategory = (creditNoteData) => {
    if (creditNoteData && creditNoteData.cae && creditNoteData.numero_comprobante) {
        return 'AFIP'; // NC A or NC B - fiscal document
    }
    return 'INTERNAL'; // NC C from PRESUPUESTO - no fiscal value
};

// Helper to check if NC A (requires IVA discrimination)
const isNotaCreditoA = (tipoComprobante) => {
    return tipoComprobante === 'NOTA DE CREDITO A';
};

function PrintQuotePage() {
    // Access the user object from the AuthContext
    const { user, isLoadingAuth, interceptorsReady } = useAuth();
    const { isBrandScopedRounding } = useFeatureFlags();

    const [cartItems, setCartItems] = useState([]);
    const [subTotalPrice, setSubTotalPrice] = useState(0);
    const [totalAfterDiscount, setTotalAfterDiscount] = useState(0);
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [organizationData, setOrganizationData] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [presupuestoData, setPresupuestoData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentDate, setCurrentDate] = useState('');
    const [clientDatabaseName, setClientDatabaseName] = useState(null);
    const [documentType, setDocumentType] = useState('PRESUPUESTO'); // 'PRESUPUESTO', 'FACTURA A', 'FACTURA B', 'FACTURA C'
    const [invoiceDetails, setInvoiceDetails] = useState(null); // CAE, CAE vencimiento, etc.
    const [documentNumber, setDocumentNumber] = useState(''); // N° for invoice/quote
    const [qrDataUrl, setQrDataUrl] = useState(''); // QR code as data URL for better print quality
    
    // Check if thermal mode is enabled via URL parameter or state
    const urlParams = new URLSearchParams(window.location.search);
    const thermalParam = urlParams.get('thermal') === 'false' ? false : true; // Default to thermal
    const [isThermalMode, setIsThermalMode] = useState(thermalParam);
    
    // Create ref for printing
    const componentRef = useRef();
    
    // Toggle thermal mode
    const toggleThermalMode = () => {
        setIsThermalMode(!isThermalMode);
    };
    
    // Use print-js like printStickerPage with inline styles matching the exact on-screen CSS
    const handlePrint = () => {
        printJS({
            printable: 'print-quote-content',
            type: 'html',
            scanStyles: false,
            style: `
                @page { margin: 5mm; }
                
                * { box-sizing: border-box; margin: 0; padding: 0; }
                
                body { margin: 0 !important; padding: 0 !important; }
                
                /* Base container styles */
                .printable-quote-container {
                    padding: 2mm !important;
                    color: #000;
                    background-color: #fff;
                    font-family: sans-serif;
                    font-size: 12pt;
                    box-sizing: border-box;
                    width: 100%;
                    max-width: 100%;
                    overflow: visible;
                }
                
                /* Thermal mode */
                .printable-quote-container.thermal-mode {
                    padding: 2mm !important;
                    margin: 0 auto !important;
                    width: 80mm !important;
                    max-width: 80mm !important;
                    font-size: 12pt !important;
                    line-height: 1.2 !important;
                }
                
                /* Organization and Client Container */
                .organization-client-container {
                    display: block;
                    text-align: center;
                    margin-bottom: 10px;
                }
                
                .dash-separator {
                    border-bottom: 1px dashed #000;
                    margin: 10px 0;
                }
                
                /* Presupuesto Mark Container */
                .presupuesto-mark-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: flex-start;
                    padding: 0 15px;
                }
                
                .header-note {
                    font-size: 8pt;
                    font-weight: bold;
                    text-align: center;
                    color: #000;
                    margin-bottom: 8px;
                    white-space: nowrap;
                    border: 1px solid #000;
                    padding: 4px 8px;
                    background-color: #fff;
                }
                
                .x-box {
                    width: 60px;
                    height: 60px;
                    border: 2px solid #000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: #fff;
                }
                
                .x-letter, .a-letter, .b-letter, .c-letter {
                    font-size: 48pt;
                    font-weight: bold;
                    color: #000;
                    line-height: 1;
                }
                
                .nc-letter {
                    font-size: 24pt;
                    font-weight: bold;
                    color: #d32f2f;
                }
                
                .factura-box {
                    border: 3px solid #000 !important;
                }
                
                .credit-note-box {
                    border: 3px solid #d32f2f !important;
                    background-color: #ffebee;
                }
                
                /* Organization Data */
                .organization-data-section {
                    text-align: center;
                    margin-bottom: 10px;
                }
                
                .organization-data-section .organization-name {
                    font-weight: bold;
                    font-size: 14pt;
                    margin-bottom: 5px;
                    color: #000;
                }
                
                .organization-data-section p {
                    font-size: 11pt;
                    margin: 3px 0;
                    color: #000;
                }
                
                /* Client Data */
                .client-data-section {
                    text-align: center;
                    margin-bottom: 10px;
                }
                
                .client-data-section .client-name {
                    font-weight: normal;
                    font-size: 11pt;
                    margin-bottom: 3px;
                    color: #000;
                }
                
                .client-data-section p {
                    font-size: 11pt;
                    margin: 3px 0;
                    color: #000;
                }
                
                /* Headers */
                h1 {
                    text-align: center;
                    color: #000;
                    font-size: 14pt;
                    font-weight: bold;
                    margin-bottom: 5px;
                    margin-top: 0;
                }
                
                .document-header {
                    text-align: center;
                    margin-bottom: 10px;
                }
                
                .document-header p {
                    margin: 3px 0;
                    font-size: 11pt;
                }
                
                .document-header .document-subtitle {
                    font-size: 8pt;
                    color: #666;
                    font-style: italic !important;
                    margin-top: 2px;
                    margin-bottom: 5px;
                }
                
                /* Paragraphs */
                p {
                    font-size: 12pt;
                    color: #000;
                    margin-top: 0;
                    margin-bottom: 0;
                }
                
                /* Divider */
                .divider {
                    border-bottom: 1px solid #000;
                    height: 1px;
                    margin-bottom: 15px;
                    margin-top: 15px;
                }
                
                /* Table */
                .printable-cart-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 10px;
                    margin-bottom: 10px;
                    font-size: 11pt;
                    color: #000;
                }
                
                .printable-cart-table th,
                .printable-cart-table td {
                    padding: 5px 3px;
                    color: #000;
                    font-size: 11pt;
                    border: none;
                }
                
                .printable-cart-table th {
                    font-weight: bold;
                    text-align: left;
                    border-bottom: 1px solid #000;
                }
                
                .printable-cart-table td {
                    text-align: left;
                    border-bottom: none;
                }
                
                .printable-cart-table .print-col-description {
                    width: 60%;
                }
                
                .printable-cart-table .print-col-price {
                    width: 25%;
                    text-align: right;
                }
                
                .printable-cart-table .print-col-quantity {
                    width: 15%;
                    text-align: center;
                }
                
                /* Price Summary */
                .total-price-summary {
                    margin-top: 5px;
                    width: 100%;
                    box-sizing: border-box;
                    padding: 0;
                    overflow: visible;
                }
                
                .summary-line {
                    font-size: 10pt;
                    color: #000;
                    margin: 3px 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                .summary-line span {
                    white-space: nowrap;
                    overflow: visible;
                }
                
                .summary-label {
                    flex: 1;
                    text-align: left;
                    padding-right: 10px;
                    min-width: 0;
                    white-space: nowrap;
                }
                
                .summary-amount {
                    flex-shrink: 0;
                    text-align: right;
                    font-weight: bold;
                    white-space: nowrap;
                }
                
                .total-line {
                    font-size: 10pt;
                    font-weight: bold;
                    color: #000;
                    margin: 3px 0;
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    width: 100%;
                    box-sizing: border-box;
                }
                
                .total-line span {
                    white-space: nowrap;
                    overflow: visible;
                }
                
                .total-separator {
                    border-top: 1px solid #000;
                    margin: 5px 0;
                }
                
                /* Thank You Message */
                .thank-you-message {
                    text-align: center;
                    font-style: italic;
                    font-size: 11pt;
                    margin-top: 10px;
                    margin-bottom: 0;
                    padding-top: 0;
                    color: #000;
                }
                
                /* CAE Section */
                .cae-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    margin-top: 10px;
                    padding: 15px;
                    border: 1px solid #ccc;
                    background-color: #f9f9f9;
                }
                
                .cae-info {
                    flex: 1;
                }
                
                .cae-info p {
                    font-size: 13pt;
                    margin: 5px 0;
                    color: #000;
                }
                
                .qr-code-container {
                    flex-shrink: 0;
                    margin-left: 20px;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                
                .qr-code {
                    width: 70px !important;
                    height: 70px !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    image-rendering: -webkit-optimize-contrast !important;
                    image-rendering: crisp-edges !important;
                }
                
                .qr-code img {
                    width: 70px !important;
                    height: 70px !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                
                /* CAE Section at Bottom */
                .cae-section-bottom {
                    margin-top: 30px;
                    padding-top: 20px;
                    display: flex !important;
                    flex-direction: row !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    gap: 10px !important;
                    width: 100% !important;
                }
                
                .cae-info-bottom {
                    text-align: left !important;
                    flex: 1 !important;
                    min-width: 0 !important;
                }
                
                .cae-info-bottom p {
                    font-size: 10pt;
                    margin: 3px 0;
                    color: #000;
                    font-weight: bold;
                }
                
                .qr-code-container-bottom {
                    width: 70px;
                    height: 70px;
                    flex-shrink: 0;
                    display: flex !important;
                    justify-content: center;
                    align-items: center;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                
                .qr-code-container-bottom .qr-code {
                    width: 70px !important;
                    height: 70px !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                }
                
                /* Credit Note Sections */
                .credit-note-section {
                    margin: 5px 2px;
                    padding: 3px;
                    border: 1px solid #d32f2f;
                    background-color: #fff;
                    border-radius: 0;
                    text-align: center;
                    box-sizing: border-box;
                    max-width: 100%;
                }
                
                .credit-note-header {
                    text-align: center;
                    margin-bottom: 10px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #d32f2f;
                }
                
                .credit-note-header h2 {
                    margin: 0;
                    color: #d32f2f;
                    font-size: 14pt;
                    font-weight: bold;
                }
                
                .credit-note-number {
                    font-size: 12pt;
                    margin: 5px 0;
                    color: #000;
                }
                
                .credit-note-details {
                    margin: 10px 0;
                    text-align: center;
                }
                
                .credit-note-detail-row {
                    display: block;
                    padding: 3px 0;
                    border-bottom: 1px solid #eee;
                    font-size: 9pt;
                    text-align: center;
                }
                
                .credit-note-detail-row strong {
                    color: #333;
                    font-size: 9pt;
                    display: block;
                    margin-bottom: 2px;
                }
                
                .credit-note-detail-row span {
                    color: #000;
                    display: block;
                    font-size: 9pt;
                }
                
                .credit-note-qr {
                    text-align: center;
                    margin: 10px 0;
                    padding: 5px 0;
                }
                
                .credit-note-qr .qr-code {
                    width: 70px !important;
                    height: 70px !important;
                    display: block !important;
                    margin: 0 auto;
                }
                
                .credit-note-qr .qr-label {
                    font-size: 8pt;
                    color: #666;
                    margin-top: 3px;
                }
            `
        });
    };

    useEffect(() => {
        // Don't fetch data if auth is still loading or interceptors not ready
        if (isLoadingAuth || !interceptorsReady) {
            console.log("[PrintQuotePage] Waiting for auth and interceptors...", { isLoadingAuth, interceptorsReady });
            return;
        }
        
        const fetchData = async () => {
            try {
                // --- Check if we have invoice ID in URL parameters ---
                // For HashRouter, query params are after the hash: /#/print-quote?invoiceId=48
                // We need to parse from window.location.hash instead of window.location.search
                const hashParts = window.location.hash.split('?');
                const queryString = hashParts.length > 1 ? hashParts[1] : window.location.search.substring(1);
                const urlParams = new URLSearchParams(queryString);
                const invoiceId = urlParams.get('invoiceId');
                const creditNoteId = urlParams.get('creditNoteId');
                const budgetQuoteId = urlParams.get('budgetQuoteId');
                
                console.log("Full URL:", window.location.href);
                console.log("Hash:", window.location.hash);
                console.log("Search:", window.location.search);
                console.log("Parsed query string:", queryString);
                console.log("Invoice ID from URL:", invoiceId);
                console.log("Credit Note ID from URL:", creditNoteId);
                console.log("Budget Quote ID from URL:", budgetQuoteId);
                
                if (budgetQuoteId) {
                    console.log("Fetching budget quote data from backend for ID:", budgetQuoteId);
                    
                    try {
                        // Fetch complete budget quote data from backend
                        const response = await axios.get(`${NODE_BACKEND_API_URL}/budget-quotes/${budgetQuoteId}`);
                        
                        console.log("Budget quote API response:", response);
                        
                        if (response.data && response.data.success) {
                            const responseData = response.data.data;
                            const quoteData = responseData.quote; // Quote details are in 'quote' property
                            const items = responseData.items || []; // Items are separate
                            
                            console.log("Budget quote details:", quoteData);
                            console.log("Budget quote items:", items);
                            
                            // Set document type to PRESUPUESTO
                            setDocumentType('PRESUPUESTO');
                            
                            // Set cart items from quote items
                            if (items.length === 0) {
                                console.error("No items found in budget quote!");
                                setError("No se encontraron productos en el presupuesto.");
                            }
                            
                            const mappedItems = items.map(item => ({
                                ProductId: item.is_custom ? null : item.product_id,
                                ProductCode: item.product_code,
                                ProductDescription: item.descripcion,
                                RetailPrice: item.precio_unitario,
                                BrandName: item.brand_name || '',
                                quantity: item.cantidad,
                                splitter: item.splitter || 1,
                                isCustom: item.is_custom === 1 || item.is_custom === true,
                            }));
                            
                            console.log("Mapped cart items:", mappedItems);
                            setCartItems(mappedItems);
                            
                            // Use quote data for totals
                            setSubTotalPrice(quoteData.subtotal || 0);
                            setTotalAfterDiscount(quoteData.total || 0);
                            setDiscountPercentage(quoteData.descuento_porcentaje || 0);
                            setDiscountAmount(quoteData.descuento_importe || 0);
                            
                            // Set client data from quote — prefer informal name as display label
                            const clientDisplayName = quoteData.cliente_nombre_informal || quoteData.cliente_razon_social;
                            if (clientDisplayName) {
                                setClientData({
                                    name: clientDisplayName,
                                    CUIT: quoteData.cliente_documento || '',
                                    condition: getClientConditionLabel(quoteData.cliente_tipo),
                                    address: quoteData.cliente_direccion || ''
                                });
                            }
                            
                            // Set presupuesto data
                            setPresupuestoData({
                                externalReference: quoteData.external_reference || '',
                                presupuestoNumero: quoteData.quote_number || '',
                                fechaEmision: quoteData.fecha_emision || '',
                                fechaVencimiento: quoteData.fecha_vencimiento || ''
                            });
                            
                            // Set document number to human-readable quote_number (e.g. "93")
                            setDocumentNumber(quoteData.quote_number || budgetQuoteId || '');
                            
                            // Set dates
                            if (quoteData.fecha_emision) {
                                const dateStr = quoteData.fecha_emision.split('T')[0];
                                const [year, month, day] = dateStr.split('-');
                                const formattedDate = `${day}/${month}/${year.slice(-2)}`;
                                setCurrentDate(formattedDate);
                            }
                            
                            // Set document title
                            if (quoteData.quote_number) {
                                document.title = `Presupuesto_${quoteData.quote_number.replace(/\//g, '-')}`;
                            }
                            
                            // Get clientDatabaseName from user context
                            setClientDatabaseName(user?.clientDatabaseName || null);
                            
                            // Get organization data from localStorage authUser
                            try {
                                const authUserString = localStorage.getItem('authUser');
                                if (authUserString) {
                                    const authUser = JSON.parse(authUserString);
                                    console.log("Auth user from localStorage:", authUser);
                                    
                                    setOrganizationData({
                                        name: authUser.organizationName || 'CASADUTTO',
                                        address: 'FALUCHO 1394',
                                        CUIT: authUser.organizationCUIT || '20-29273214-4',
                                        phone: '+54 0348-4589723'
                                    });
                                } else {
                                    // Fallback if no authUser in localStorage
                                    setOrganizationData({
                                        name: 'CASADUTTO',
                                        address: 'FALUCHO 1394',
                                        CUIT: '20-29273214-4',
                                        phone: '+54 0348-4589723'
                                    });
                                }
                            } catch (orgError) {
                                console.error("Error parsing authUser from localStorage:", orgError);
                                // Fallback organization data on error
                                setOrganizationData({
                                    name: 'CASADUTTO',
                                    address: 'FALUCHO 1394',
                                    CUIT: '20-29273214-4',
                                    phone: '+54 0348-4589723'
                                });
                            }
                            
                            setLoading(false);
                        } else {
                            throw new Error('No se recibieron datos del presupuesto');
                        }
                    } catch (err) {
                        console.error("Error loading budget quote data:", err);
                        setError(err.response?.data?.message || err.message || "Error al cargar el presupuesto");
                        setLoading(false);
                    }
                } else if (creditNoteId) {
                    console.log("Loading credit note data from sessionStorage for ID:", creditNoteId);
                    
                    try {
                        // Get credit note data from sessionStorage (passed from creditNotesPage)
                        const creditNoteDataString = sessionStorage.getItem('printCreditNoteData');
                        
                        if (!creditNoteDataString || creditNoteDataString === 'undefined' || creditNoteDataString === 'null') {
                            throw new Error('No se encontraron datos de la nota de crédito en la sesión');
                        }
                        
                        const creditNoteData = JSON.parse(creditNoteDataString);
                        console.log("Credit note data loaded from sessionStorage:", creditNoteData);
                        console.log("Credit note items:", creditNoteData.items);
                        console.log("Credit note total:", creditNoteData.total);
                        console.log("DEBUG - original_invoice_numero_comprobante:", creditNoteData.original_invoice_numero_comprobante);
                        console.log("DEBUG - All keys in creditNoteData:", Object.keys(creditNoteData));
                        
                        if (!creditNoteData || typeof creditNoteData !== 'object') {
                            throw new Error('Datos de nota de crédito inválidos');
                        }
                        
                        // Clean up sessionStorage after retrieving
                        sessionStorage.removeItem('printCreditNoteData');
                        
                        // Set document type to credit note
                        let tipoComprobante = creditNoteData.tipo_comprobante || 'NOTA DE CREDITO';
                        // Replace NC C (internal) with NC X
                        if (tipoComprobante === 'NOTA DE CREDITO C' || (!creditNoteData.cae && !creditNoteData.numero_comprobante)) {
                            tipoComprobante = 'NOTA DE CREDITO X';
                        }
                        setDocumentType(tipoComprobante);
                        
                        // Set document number (numero_comprobante for credit notes)
                        setDocumentNumber(creditNoteData.numero_comprobante || '');
                        
                        // Fetch original invoice data to get client name and invoice number
                        let clientName = creditNoteData.cliente_razon_social || 'Consumidor Final';
                        let originalInvoiceNumber = creditNoteData.original_invoice_numero_comprobante || '';
                        
                        if (creditNoteData.original_invoice_id) {
                            try {
                                console.log("Fetching original invoice data for client name and number:", creditNoteData.original_invoice_id);
                                const token = localStorage.getItem('authToken');
                                const invoiceResponse = await axios.get(
                                    `${NODE_BACKEND_API_URL}/invoices/${creditNoteData.original_invoice_id}`,
                                    { headers: { 'Authorization': `Bearer ${token}` } }
                                );
                                
                                if (invoiceResponse.data?.success && invoiceResponse.data?.data) {
                                    const invoiceData = invoiceResponse.data.data;
                                    clientName = invoiceData.cliente_razon_social || clientName;
                                    originalInvoiceNumber = invoiceData.numero_comprobante || originalInvoiceNumber;
                                    console.log("Client name from original invoice:", clientName);
                                    console.log("Invoice number from original invoice:", originalInvoiceNumber);
                                }
                            } catch (err) {
                                console.warn("Could not fetch original invoice data:", err);
                                // Continue with fallback values
                            }
                        }
                        
                        // Set invoice details for credit note (CAE, QR, etc.) - with fetched invoice number
                        setInvoiceDetails({
                            cae: creditNoteData.cae || '',
                            caeVencimiento: creditNoteData.cae_vencimiento || '',
                            puntoVenta: creditNoteData.punto_venta || '',
                            afipQr: creditNoteData.afip_qr || creditNoteData.qr_code || '',
                            numeroComprobante: creditNoteData.numero_comprobante || '',
                            originalInvoiceId: creditNoteData.original_invoice_id || null,
                            originalInvoiceNumber: originalInvoiceNumber,
                            // NEW: Add these fields for AFIP compliance
                            subtotal: creditNoteData.subtotal || 0,
                            total: creditNoteData.total || 0,
                            cancellationType: creditNoteData.cancellation_type || 'full',
                            reason: creditNoteData.reason || 'Anulación de comprobante',
                            isInternal: !creditNoteData.cae && !creditNoteData.numero_comprobante
                        });
                        
                        console.log("DEBUG - Invoice details set:", {
                            originalInvoiceId: creditNoteData.original_invoice_id,
                            originalInvoiceNumber: originalInvoiceNumber
                        });
                        
                        // Set client data from credit note (with fetched name if available)
                        setClientData({
                            name: clientName,
                            CUIT: creditNoteData.cliente_documento || '',
                            condition: getClientConditionLabel(creditNoteData.cliente_tipo),
                            address: ''
                        });
                        
                        // Set presupuesto data (repurposed for credit note)
                        setPresupuestoData({
                            externalReference: creditNoteData.external_reference || '',
                            presupuestoNumero: creditNoteData.numero_comprobante || '',
                            fechaEmision: creditNoteData.fecha_emision || '',
                            fechaVencimiento: creditNoteData.cae_vencimiento || ''
                        });
                        
                        // Set dates
                        if (creditNoteData.fecha_emision) {
                            const dateStr = creditNoteData.fecha_emision.split('T')[0];
                            const [year, month, day] = dateStr.split('-');
                            const formattedDate = `${day}/${month}/${year.slice(-2)}`;
                            setCurrentDate(formattedDate);
                        }
                        
                        // Set document title
                        if (creditNoteData.numero_comprobante) {
                            document.title = `Nota_Credito_${creditNoteData.numero_comprobante.replace(/\//g, '-')}`;
                        }
                        
                        // Map credit note items to cart items format for display
                        if (creditNoteData.items && Array.isArray(creditNoteData.items)) {
                            const mappedItems = creditNoteData.items.map(item => ({
                                productId: item.product_id || 0,
                                ProductId: item.product_id || 0,
                                ProductCode: item.product_code || '',
                                ProductDescription: item.product_description || item.description || 'Sin descripción',
                                Descripcion: item.product_description || item.description || 'Sin descripción',
                                RetailPrice: parseFloat(item.unit_price || 0),
                                PrecioVenta: parseFloat(item.unit_price || 0),
                                quantity: parseFloat(item.quantity || item.cantidad || 0),
                                splitter: 1,
                                total: parseFloat(item.subtotal || item.total || 0),
                                ivaRate: parseFloat(item.iva_rate || item.IVA || 21),
                                finalPrice: parseFloat(item.unit_price || 0)
                            }));
                            setCartItems(mappedItems);
                            
                            // For credit notes, use API totals directly (not calculated from items)
                            // This ensures we display the official AFIP-registered amounts
                            const creditNoteSubtotal = parseFloat(creditNoteData.subtotal || creditNoteData.total || 0);
                            const creditNoteTotal = parseFloat(creditNoteData.total || 0);
                            
                            setSubTotalPrice(creditNoteSubtotal);
                            setTotalAfterDiscount(creditNoteTotal);
                        } else {
                            // If no items, set empty array
                            setCartItems([]);
                            setSubTotalPrice(creditNoteData.total || 0);
                            setTotalAfterDiscount(creditNoteData.total || 0);
                        }
                        
                        // Get clientDatabaseName from user context
                        setClientDatabaseName(user?.clientDatabaseName || null);
                        
                        // Fetch organization data from /getclientquotedata endpoint
                        try {
                            const orgResponse = await axios.get(`${NODE_BACKEND_API_URL}/getclientquotedata`);
                            if (orgResponse.data) {
                                console.log("Organization data:", orgResponse.data);
                                
                                // Handle if response is an array (take first element)
                                const orgData = Array.isArray(orgResponse.data) ? orgResponse.data[0] : orgResponse.data;
                                
                                if (orgData) {
                                    // Clean up the data (remove \r\n and extra spaces)
                                    setOrganizationData({
                                        name: (orgData.Name || orgData.name || '').replace(/[\r\n]/g, '').trim(),
                                        address: (orgData.Address || orgData.address || '').replace(/[\r\n]/g, '').trim(),
                                        CUIT: (orgData.CUIT || orgData.cuit || '').replace(/[\r\n]/g, '').trim(),
                                        phone: (orgData.Phone || orgData.phone || '').replace(/[\r\n]/g, '').trim()
                                    });
                                } else {
                                    throw new Error('No organization data in response');
                                }
                            }
                        } catch (orgError) {
                            console.error("Error fetching organization data:", orgError);
                            
                            // Fallback: Try to get organization data from localStorage authUser
                            try {
                                const authUserString = localStorage.getItem('authUser');
                                if (authUserString) {
                                    const authUser = JSON.parse(authUserString);
                                    console.log("Using authUser from localStorage for credit note:", authUser);
                                    
                                    setOrganizationData({
                                        name: authUser.organizationName || 'CASADUTTO',
                                        address: 'FALUCHO 1394',
                                        CUIT: authUser.organizationCUIT || '20-29273214-4',
                                        phone: '+54 0348-4589723'
                                    });
                                } else {
                                    // Final fallback
                                    setOrganizationData({
                                        name: 'CASADUTTO',
                                        address: 'FALUCHO 1394',
                                        CUIT: '20-29273214-4',
                                        phone: '+54 0348-4589723'
                                    });
                                }
                            } catch (fallbackError) {
                                console.error("Error parsing authUser from localStorage:", fallbackError);
                                // Absolute fallback
                                setOrganizationData({
                                    name: 'CASADUTTO',
                                    address: 'FALUCHO 1394',
                                    CUIT: '20-29273214-4',
                                    phone: '+54 0348-4589723'
                                });
                            }
                        }
                        
                        setLoading(false);
                        
                    } catch (err) {
                        console.error("Error loading credit note data:", err);
                        setError(err.message || "Error al cargar la nota de crédito");
                        setLoading(false);
                    }
                } else if (invoiceId) {
                    console.log("Fetching invoice data from backend for ID:", invoiceId);
                    console.log("[PrintQuotePage] Using axios instance:", axios);
                    console.log("[PrintQuotePage] Request URL:", `${NODE_BACKEND_API_URL}/invoices/${invoiceId}`);
                    
                    try {
                        // Fetch complete invoice data from backend
                        // Invoice endpoints are under /apitest/invoices
                        const response = await axios.get(`${NODE_BACKEND_API_URL}/invoices/${invoiceId}`);
                        
                        console.log("Full API response:", response);
                        console.log("Response data:", response.data);
                        
                        if (response.data && response.data.success) {
                            const invoiceData = response.data.data;
                            console.log("Invoice data received:", invoiceData);
                            console.log("Invoice items:", invoiceData.items);
                            
                            // Set cart items from invoice items
                            const items = invoiceData.items || [];
                            
                            if (items.length === 0) {
                                console.error("No items found in invoice data!");
                                setError("No se encontraron productos en el presupuesto.");
                            }
                            
                            const mappedItems = items.map(item => {
                                console.log("Mapping item:", item);
                                // Clean -test_de_servicio suffix from legacy data
                                const cleanDescription = item.descripcion ? item.descripcion.replace(/-test_de_servicio$/i, '') : item.descripcion;
                                // invoice_items stores precio_unitario already divided by splitter, sin IVA (AFIP standard).
                                // The renderer displays:  PRECIO = RetailPrice / splitter.
                                // Per ARCA Ley de Transparencia Fiscal:
                                //   - A / NC A → display NETO (sin IVA)
                                //   - B / C / Presupuesto / NC B / NC X → display FINAL (IVA incluido)
                                const itemSplitter = item.splitter || 1;
                                const ivaMultiplier = shouldShowFinalPrice(invoiceData.tipo_comprobante)
                                    ? (1 + (item.alicuota_iva || 21) / 100)
                                    : 1;
                                return {
                                    ProductId: item.product_id,
                                    ProductCode: item.product_code,
                                    ProductDescription: cleanDescription,
                                    RetailPrice: item.precio_unitario * itemSplitter * ivaMultiplier,
                                    BrandName: item.brand_name,
                                    quantity: item.cantidad,
                                    splitter: itemSplitter
                                };
                            });
                            
                            console.log("Mapped cart items:", mappedItems);
                            setCartItems(mappedItems);
                            
                            // Backend stores:
                            // - subtotal: WITHOUT IVA, BEFORE discount
                            // - descuento_importe: discount amount (not percentage calculation)
                            // - total: WITH IVA, AFTER discount
                            const subtotalBeforeDiscount = invoiceData.subtotal || 0;
                            const discountAmount = invoiceData.descuento_importe || 0;
                            const discountPct = invoiceData.descuento_porcentaje || 0;
                            
                            // Calculate subtotal AFTER discount (what should display on ticket)
                            const subtotalAfterDiscount = subtotalBeforeDiscount - discountAmount;
                            
                            console.log('💰 [Print Page] Financial breakdown:', {
                                subtotalBeforeDiscount,
                                discountAmount,
                                discountPct,
                                subtotalAfterDiscount,
                                total: invoiceData.total,
                                calculatedIVA: subtotalAfterDiscount * 0.21
                            });
                            
                            setSubTotalPrice(subtotalAfterDiscount);
                            setTotalAfterDiscount(invoiceData.total || 0);
                            setDiscountPercentage(discountPct);
                            setDiscountAmount(discountAmount);
                            
                            // Set document type from invoice
                            const tipoComprobante = invoiceData.tipo_comprobante || 'PRESUPUESTO';
                            setDocumentType(tipoComprobante);
                            
                            // Set invoice details (CAE, payment breakdown, etc.) if it's a factura
                            if (tipoComprobante.includes('FACTURA')) {
                                // Parse payment breakdown if available
                                let paymentBreakdown = null;
                                if (invoiceData.payment_breakdown) {
                                    try {
                                        paymentBreakdown = typeof invoiceData.payment_breakdown === 'string'
                                            ? JSON.parse(invoiceData.payment_breakdown)
                                            : invoiceData.payment_breakdown;
                                    } catch (e) {
                                        console.warn('Could not parse payment_breakdown:', e);
                                    }
                                }
                                
                                setInvoiceDetails({
                                    numeroComprobante: invoiceData.numero_comprobante || '',
                                    cae: invoiceData.cae || '',
                                    caeVencimiento: invoiceData.cae_vencimiento || '',
                                    puntoVenta: invoiceData.punto_venta || '',
                                    afipQr: invoiceData.afip_qr || '',
                                    paymentMethod: invoiceData.payment_method || '',
                                    paymentBreakdown: paymentBreakdown
                                });
                                
                                // Set document number (numero_comprobante for facturas)
                                setDocumentNumber(invoiceData.numero_comprobante || '');
                            }
                            
                            // Set client data from invoice
                            setClientData({
                                name: invoiceData.cliente_razon_social || '',
                                CUIT: invoiceData.cliente_documento || '',
                                condition: getClientConditionLabel(invoiceData.cliente_tipo),
                                address: '' // Not available in invoice data
                            });
                            
                            // Set presupuesto data from invoice
                            setPresupuestoData({
                                externalReference: invoiceData.external_reference || '',
                                presupuestoNumero: invoiceData.presupuesto_numero || invoiceData.numero_comprobante || '',
                                fechaEmision: invoiceData.fecha_emision || '',
                                fechaVencimiento: invoiceData.fecha_vencimiento || ''
                            });
                            
                            // Set dates
                            if (invoiceData.fecha_emision) {
                                // Parse as UTC date to avoid timezone issues
                                const dateStr = invoiceData.fecha_emision.split('T')[0]; // Get just the date part: "2025-11-07"
                                const [year, month, day] = dateStr.split('-');
                                const formattedDate = `${day}/${month}/${year.slice(-2)}`;
                                setCurrentDate(formattedDate);
                            }
                            
                            // Set document title
                            if (invoiceData.presupuesto_numero) {
                                document.title = `Presupuesto_${invoiceData.presupuesto_numero.replace(/\//g, '-')}`;
                            } else if (invoiceData.numero_comprobante) {
                                document.title = `Factura_${invoiceData.numero_comprobante.replace(/\//g, '-')}`;
                            }
                            
                            // Get clientDatabaseName for pricing
                            setClientDatabaseName(invoiceData.client_database_name);
                            
                            // Fetch organization data from /getclientquotedata endpoint
                            try {
                                const orgResponse = await axios.get(`${NODE_BACKEND_API_URL}/getclientquotedata`);
                                if (orgResponse.data && Array.isArray(orgResponse.data) && orgResponse.data.length > 0) {
                                    const orgData = orgResponse.data[0];
                                    setOrganizationData({
                                        name: orgData.Name || '',
                                        address: orgData.Address || '',
                                        CUIT: orgData.CUIT || '',
                                        phone: orgData.Phone || ''
                                    });
                                }
                            } catch (orgErr) {
                                console.warn("Could not fetch organization data:", orgErr);
                                // Fallback to authToken for organization name
                                const authToken = localStorage.getItem('authToken');
                                if (authToken) {
                                    try {
                                        const parsedToken = JSON.parse(authToken);
                                        setOrganizationData({
                                            name: parsedToken.organizationName || '',
                                            address: '',
                                            CUIT: '',
                                            phone: ''
                                        });
                                    } catch (e) {
                                        console.error('Error parsing authToken:', e);
                                    }
                                }
                            }
                            
                            setLoading(false);
                            return;
                        } else {
                            console.error("Invalid response from backend:", response.data);
                            setError("Error al obtener datos de la factura del servidor.");
                        }
                    } catch (err) {
                        console.error("Failed to fetch invoice data from backend:", err);
                        if (err.response && err.response.status === 401) {
                            setError("Sesión expirada. Por favor, inicie sesión nuevamente.");
                        } else if (err.response && err.response.status === 404) {
                            setError("Presupuesto no encontrado.");
                        } else {
                            setError("Error al obtener datos de la factura del servidor.");
                        }
                    }
                }
                
                // --- Fallback: Check for encoded data in URL (old method) ---
                const encodedData = urlParams.get('data');
                
                if (encodedData) {
                    try {
                        // Decode base64 data from URL
                        const dataString = decodeURIComponent(atob(encodedData));
                        const invoiceData = JSON.parse(dataString);
                        console.log("Loading presupuesto data from URL:", invoiceData);
                        
                        setCartItems(invoiceData.cartItems || []);
                        setSubTotalPrice(invoiceData.subtotal || 0);
                        setTotalAfterDiscount(invoiceData.total || 0);
                        setDiscountPercentage(invoiceData.discountPercentage || 0);
                        setDiscountAmount(invoiceData.discountAmount || 0);
                        
                        // Set organization data from invoice
                        setOrganizationData({
                            name: invoiceData.organizationName || '',
                            address: invoiceData.organizationAddress || '',
                            CUIT: invoiceData.organizationCUIT || '',
                            phone: invoiceData.organizationPhone || ''
                        });
                        
                        // Set client data from invoice
                        setClientData(invoiceData.clientData || null);
                        
                        // Set presupuesto data from invoice
                        setPresupuestoData({
                            externalReference: invoiceData.externalReference || '',
                            presupuestoNumero: invoiceData.presupuestoNumero || '',
                            fechaEmision: invoiceData.fechaEmision || '',
                            fechaVencimiento: invoiceData.fechaVencimiento || ''
                        });
                        
                        // Set dates from invoice
                        if (invoiceData.fechaEmision) {
                            const emisionDate = new Date(invoiceData.fechaEmision);
                            const formattedEmision = `${String(emisionDate.getDate()).padStart(2, '0')}/${String(emisionDate.getMonth() + 1).padStart(2, '0')}/${emisionDate.getFullYear().toString().slice(-2)}`;
                            setCurrentDate(formattedEmision);
                        }
                        
                        // Set document title with presupuesto number
                        if (invoiceData.presupuestoNumero) {
                            document.title = `Presupuesto_${invoiceData.presupuestoNumero.replace(/\//g, '-')}`;
                        }
                        
                        setLoading(false);
                        return;
                    } catch (err) {
                        console.error("Failed to parse invoice data from URL:", err);
                        setError("Error al decodificar datos del presupuesto.");
                    }
                }
                
                // --- Fallback: Check localStorage for backward compatibility ---
                let savedInvoiceData = localStorage.getItem('printInvoiceData');
                const dataTimestamp = localStorage.getItem('printInvoiceDataTimestamp');
                
                // If no data yet, wait a bit and try again (timing issue with new tab)
                if (!savedInvoiceData) {
                    console.log("No invoice data found yet, waiting 200ms...");
                    await new Promise(resolve => setTimeout(resolve, 200));
                    savedInvoiceData = localStorage.getItem('printInvoiceData');
                }
                
                if (savedInvoiceData) {
                    // Use invoice data directly
                    try {
                        const invoiceData = JSON.parse(savedInvoiceData);
                        console.log("Loading presupuesto data from localStorage:", invoiceData);
                        
                        setCartItems(invoiceData.cartItems || []);
                        setSubTotalPrice(invoiceData.subtotal || 0);
                        setTotalAfterDiscount(invoiceData.total || 0);
                        setDiscountPercentage(invoiceData.discountPercentage || 0);
                        setDiscountAmount(invoiceData.discountAmount || 0);
                        
                        // Set organization data from invoice
                        setOrganizationData({
                            name: invoiceData.organizationName || '',
                            address: invoiceData.organizationAddress || '',
                            CUIT: invoiceData.organizationCUIT || '',
                            phone: invoiceData.organizationPhone || ''
                        });
                        
                        // Set client data from invoice
                        setClientData(invoiceData.clientData || null);
                        
                        // Set presupuesto data from invoice
                        setPresupuestoData({
                            externalReference: invoiceData.externalReference || '',
                            presupuestoNumero: invoiceData.presupuestoNumero || '',
                            fechaEmision: invoiceData.fechaEmision || '',
                            fechaVencimiento: invoiceData.fechaVencimiento || ''
                        });
                        
                        // Set dates from invoice
                        if (invoiceData.fechaEmision) {
                            const emisionDate = new Date(invoiceData.fechaEmision);
                            const formattedEmision = `${String(emisionDate.getDate()).padStart(2, '0')}/${String(emisionDate.getMonth() + 1).padStart(2, '0')}/${emisionDate.getFullYear().toString().slice(-2)}`;
                            setCurrentDate(formattedEmision);
                        }
                        
                        // Set document title with presupuesto number
                        if (invoiceData.presupuestoNumero) {
                            document.title = `Presupuesto_${invoiceData.presupuestoNumero.replace(/\//g, '-')}`;
                        }
                        
                        // Clean up localStorage after 10 seconds (to allow printing/viewing)
                        setTimeout(() => {
                            localStorage.removeItem('printInvoiceData');
                            localStorage.removeItem('printInvoiceDataTimestamp');
                        }, 10000);
                        
                        setLoading(false);
                        return;
                    } catch (err) {
                        console.error("Failed to parse invoice data from localStorage:", err);
                    }
                }
                
                // --- Load cart data from sessionStorage (old flow) ---
                const savedCartItems = sessionStorage.getItem('printCartItems');
                const savedSubTotalPrice = sessionStorage.getItem('printSubTotalPrice');
                const savedTotalAfterDiscount = sessionStorage.getItem('printTotalAfterDiscount');
                const savedDiscountPercentage = sessionStorage.getItem('printDiscountPercentage');

                // Get clientDatabaseName from localStorage synchronously, just like in CartProvider
                const authUserString = localStorage.getItem('authUser');
                let currentClientDbName = null;
                if (authUserString) {
                    try {
                        const authUser = JSON.parse(authUserString);
                        currentClientDbName = authUser?.clientDatabaseName || null;
                    } catch (e) {
                        console.error("PrintQuotePage: Error parsing authUser from localStorage for clientDatabaseName:", e);
                    }
                }
                setClientDatabaseName(currentClientDbName);


                if (savedCartItems && savedSubTotalPrice && savedTotalAfterDiscount) {
                    try {
                        // Ensure splitter is parsed correctly, default to 1
                        const parsedCartItems = JSON.parse(savedCartItems).map(item => ({
                            ...item,
                            splitter: item.splitter || 1 // Ensure splitter is always at least 1
                        }));
                        const parsedSubTotalPrice = parseFloat(savedSubTotalPrice);
                        const parsedTotalAfterDiscount = parseFloat(savedTotalAfterDiscount);
                        const parsedDiscountPercentage = parseFloat(savedDiscountPercentage);

                        if (
                            Array.isArray(parsedCartItems) &&
                            !isNaN(parsedSubTotalPrice) &&
                            !isNaN(parsedTotalAfterDiscount) &&
                            !isNaN(parsedDiscountPercentage)
                        ) {
                            setCartItems(parsedCartItems);
                            setSubTotalPrice(parsedSubTotalPrice);
                            setTotalAfterDiscount(parsedTotalAfterDiscount);
                            setDiscountPercentage(parsedDiscountPercentage);
                            console.log("Loaded print data from sessionStorage.");
                        } else {
                            console.error("Invalid print data format in sessionStorage.");
                            setError("Error: Datos del carrito incompletos o inválidos.");
                        }
                    } catch (err) {
                        console.error("Failed to parse print data from sessionStorage:", err);
                        setError("Error al procesar datos del carrito.");
                    }
                } else {
                    console.warn("No complete print data found in sessionStorage.");
                }

                // Clean up sessionStorage after retrieving
                sessionStorage.removeItem('printCartItems');
                sessionStorage.removeItem('printSubTotalPrice');
                sessionStorage.removeItem('printTotalAfterDiscount');
                sessionStorage.removeItem('printDiscountPercentage');


                // --- Fetch organization data from localStorage ---
                const authToken = localStorage.getItem('authToken');
                if (authToken) {
                    try {
                        // Check if authToken is already an object or needs parsing
                        let parsedToken;
                        if (typeof authToken === 'string') {
                            // Try to parse only if it looks like JSON
                            if (authToken.trim().startsWith('{')) {
                                parsedToken = JSON.parse(authToken);
                            } else {
                                console.warn('authToken is not JSON, skipping parse');
                                parsedToken = null;
                            }
                        } else {
                            parsedToken = authToken;
                        }
                        
                        if (parsedToken && typeof parsedToken === 'object') {
                            setOrganizationData({
                                name: parsedToken.organizationName || '',
                                address: '', // Not available in authToken
                                CUIT: '', // Not available in authToken
                                phone: '' // Not available in authToken
                            });
                        }
                    } catch (e) {
                        console.error('Error parsing authToken:', e);
                        // Don't crash, just skip setting organization data from token
                    }
                }

                // --- Fetch client quote data for organization ---
                if (user) {
                    console.log("Fetching client quote data...");
                    const clientDataUrl = `${NODE_BACKEND_API_URL}/getclientquotedata`;
                    const response = await axios.get(clientDataUrl);

                    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                        const fetchedClientData = response.data[0];
                        console.log("Client data fetched:", fetchedClientData);
                        setOrganizationData((prevData) => ({
                            ...prevData,
                            address: fetchedClientData.Address || '',
                            CUIT: fetchedClientData.CUIT || '',
                            phone: fetchedClientData.Phone || ''
                        }));
                    } else {
                        console.warn("No client data received from API or data is empty/invalid.");
                    }
                } else {
                    console.warn("User not authenticated or user object not available. Cannot fetch client data.");
                }

            } catch (err) {
                console.error("Error fetching client data:", err);
                if (axios.isAxiosError(err) && err.response) {
                    setError(`Error al obtener datos del cliente: ${err.response.status} - ${err.response.data.message || 'Error desconocido'}`);
                } else {
                    setError("Error de red o desconocido al obtener datos del cliente.");
                }
            } finally {
                setLoading(false);
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear().toString().slice(-2);
                const formattedDate = `${day}/${month}/${year}`; // Format as dd/mm/yy
                setCurrentDate(formattedDate); // Set the current date state

                const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).replace(/:/g, '-');
                document.title = `Cotizacion_${day}-${month}-${year}_${time}`; // Keep original document title format
            }
        };

        // Add small delay to ensure interceptors are fully attached
        const timer = setTimeout(() => {
            fetchData();
        }, 100);
        
        // Cleanup timer on unmount or dependency change
        return () => clearTimeout(timer);
    }, [user, isLoadingAuth, interceptorsReady]);
    
    useEffect(() => {
        if (invoiceDetails?.afipQr) {
            QRCode.toDataURL(invoiceDetails.afipQr, {
                errorCorrectionLevel: 'L',
                width: 512, // Optimized for smaller footprint
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                }
            }).then(url => {
                setQrDataUrl(url);
                console.log('✅ QR code data URL generated');
            }).catch(err => {
                console.error('❌ Error generating QR code:', err);
            });
        }
    }, [invoiceDetails?.afipQr]);

    if (loading) {
        return <div>Cargando datos de la cotización...</div>;
    }

    if (error) {
        return <div className="error-message">Error: {error}</div>;
    }

    return (
        <>
            {/* Print Controls at the Top */}
            <div className="print-controls-top">
                <button 
                    onClick={handlePrint}
                    disabled={loading || !cartItems.length || (invoiceDetails?.afipQr && !qrDataUrl)}
                    style={{ 
                        backgroundColor: (loading || !cartItems.length || (invoiceDetails?.afipQr && !qrDataUrl)) ? '#ccc' : '#28a745',
                        color: 'white',
                        cursor: (loading || !cartItems.length || (invoiceDetails?.afipQr && !qrDataUrl)) ? 'not-allowed' : 'pointer',
                        opacity: (loading || !cartItems.length || (invoiceDetails?.afipQr && !qrDataUrl)) ? 0.6 : 1
                    }}
                    title={
                        loading ? 'Cargando datos...' : 
                        !cartItems.length ? 'No hay productos para imprimir' :
                        (invoiceDetails?.afipQr && !qrDataUrl) ? 'Generando código QR...' :
                        'Imprimir comprobante'
                    }
                >
                    🎫 Imprimir Ticket
                </button>
                <button onClick={() => window.close()} style={{ backgroundColor: '#f5d540', color: 'black' }}>Cerrar</button>
            </div>
            
            {/* Centered Preview Container */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <div id="print-quote-content" ref={componentRef} className={`printable-quote-container ${isThermalMode ? 'thermal-mode' : ''}`}>
                    {/* Organization and Client Data - Centered Thermal Style */}
                    <div className="organization-client-container">
                        {/* Organization Data */}
                        {organizationData && (
                            <div className="organization-data-section">
                                <p className="organization-name">{organizationData.name || 'No disponible'}</p>
                                <p>CUIT: {organizationData.CUIT || 'No disponible'}</p>
                                <p>Teléfono: {organizationData.phone || 'No disponible'}</p>
                            </div>
                        )}
                        
                        {/* Document Header */}
                        <div className="document-header">
                            <h1>{documentType === 'PRESUPUESTO' ? 'PROFORMA' : documentType}</h1>
                            {documentNumber && !documentType.includes('NOTA DE CREDITO') && (
                                <p style={{ fontSize: '11pt', fontWeight: 'bold', margin: '5px 0' }}>N°: {documentNumber}</p>
                            )}
                            {/* Credit Note Header - Enhanced with AFIP compliance */}
                            {(documentType.includes('NOTA DE CREDITO') || documentType.includes('NOTA DE CRÉDITO')) && (
                                <>
                                    {/* Show comprobante number or internal ID */}
                                    <p style={{ fontSize: '11pt', fontWeight: 'bold', margin: '5px 0' }}>
                                        N°: {documentNumber || `NC-${invoiceDetails?.originalInvoiceId || ''}`}
                                    </p>
                                    
                                    {/* Show cancellation type */}
                                    <p style={{ fontSize: '10pt', margin: '3px 0' }}>
                                        Tipo: {invoiceDetails?.cancellationType === 'full' ? 'Anulación Total' : 'Anulación Parcial'}
                                    </p>
                                    
                                    {/* Show client name */}
                                    {clientData?.name && (
                                        <p style={{ fontSize: '10pt', margin: '3px 0' }}>Cliente: {clientData.name}</p>
                                    )}
                                    
                                    {/* Show original invoice reference */}
                                    {invoiceDetails?.originalInvoiceNumber && (
                                        <p style={{ fontSize: '10pt', margin: '3px 0' }}>
                                            Factura Original: {invoiceDetails.originalInvoiceNumber}
                                        </p>
                                    )}
                                    {!invoiceDetails?.originalInvoiceNumber && invoiceDetails?.originalInvoiceId && (
                                        <p style={{ fontSize: '10pt', margin: '3px 0' }}>
                                            Presupuesto Original: #{invoiceDetails.originalInvoiceId}
                                        </p>
                                    )}
                                </>
                            )}
                            <p>FECHA: {currentDate}</p>
                            {presupuestoData && presupuestoData.fechaVencimiento && (
                                <p>Vencimiento: {new Date(presupuestoData.fechaVencimiento).toLocaleDateString('es-AR')}</p>
                            )}
                        </div>
                        
                        <div className="dash-separator"></div>
                        
                        {/* Client Data - Only for Facturas and Notas de Crédito */}
                        {documentType !== 'PRESUPUESTO' && (
                            <>
                                {documentType === 'FACTURA B' || documentType === 'FACTURA C' ? (
                                    <div className="client-data-section">
                                        {/* Show client name for Factura B/C when client is declared (not anonymous) */}
                                        {clientData?.name && clientData.condition !== 'CONSUMIDOR FINAL' && (
                                            <p>Cliente: {clientData.name}</p>
                                        )}
                                        <p>Condición: {clientData?.condition || 'CONSUMIDOR FINAL'}</p>
                                    </div>
                                ) : clientData && clientData.name && !(documentType.includes('NOTA DE CREDITO') || documentType.includes('NOTA DE CRÉDITO')) && (
                                    <div className="client-data-section">
                                        <p>Cliente: {clientData.name}</p>
                                        {documentType === 'FACTURA A' && (
                                            <>
                                                <p>CUIT: {(clientData.CUIT && clientData.CUIT !== '0') ? clientData.CUIT : 'No disponible'}</p>
                                                <p>Condición: {clientData.condition || 'No disponible'}</p>
                                            </>
                                        )}
                                    </div>
                                )}
                                {!(documentType.includes('NOTA DE CREDITO') || documentType.includes('NOTA DE CRÉDITO')) && (
                                    <div className="dash-separator"></div>
                                )}
                            </>
                        )}
                    </div>
            
            {/* Credit Note Section - Only for AFIP-registered (NC A/B with CAE) */}
            {invoiceDetails && (documentType.includes('NOTA DE CREDITO') || documentType.includes('NOTA DE CRÉDITO')) && !invoiceDetails.isInternal && (
                <>
                    <div className="credit-note-section">
                        <div className="credit-note-details">
                            {/* NEW: Show reason */}
                            {invoiceDetails.reason && (
                                <div className="credit-note-detail-row">
                                    <strong>Motivo:</strong> 
                                    <span>{invoiceDetails.reason}</span>
                                </div>
                            )}
                            
                            {invoiceDetails.cae && (
                                <div className="credit-note-detail-row">
                                    <strong>CAE:</strong> 
                                    <span style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                        {invoiceDetails.cae}
                                    </span>
                                </div>
                            )}
                            
                            {invoiceDetails.caeVencimiento && (
                                <div className="credit-note-detail-row">
                                    <strong>Vencimiento CAE:</strong> 
                                    <span>
                                        {new Date(invoiceDetails.caeVencimiento).toLocaleDateString('es-AR')}
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        {/* Only show QR if afipQr exists and is a URL */}
                        {invoiceDetails.afipQr && invoiceDetails.afipQr.startsWith('http') && qrDataUrl && (
                            <div className="credit-note-qr">
                                <img 
                                    src={qrDataUrl}
                                    alt="Código QR AFIP"
                                    className="qr-code"
                                />
                                <p className="qr-label">Código QR AFIP</p>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* NC X Internal Note - Show reason only */}
            {invoiceDetails && (documentType.includes('NOTA DE CREDITO') || documentType.includes('NOTA DE CRÉDITO')) && invoiceDetails.isInternal && (
                <div className="credit-note-section" style={{ marginTop: '16px', marginBottom: '16px' }}>
                    <div className="credit-note-details">
                        {invoiceDetails.reason && (
                            <div className="credit-note-detail-row">
                                <strong>Motivo:</strong> 
                                <span>{invoiceDetails.reason}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cart section - show items for all document types including credit notes */}
            {cartItems.length === 0 ? (
                <p>El carrito está vacío.</p>
            ) : (
                <>
                    <table className="printable-cart-table">
                        <thead>
                            <tr>
                                <th className="print-col-description">DESCRIPCION</th>
                                <th className="print-col-price">PRECIO</th>
                                <th className="print-col-quantity">CANT.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cartItems.map((item, index) => {
                                const roundedPrice = calculateRoundedRetailPrice(item.RetailPrice, clientDatabaseName, isBrandScopedRounding);
                                const splitter = item.splitter || 1;
                                const quantity = item.quantity || 1;

                                // Merge product code and description
                                const mergedDescription = `${item.ProductCode || ''} ${item.ProductDescription || ''}`;

                                return (
                                    <tr key={`${item.productId}-${index}`}>
                                        <td className="print-col-description">{mergedDescription}</td>
                                        <td className="print-col-price">
                                            {/* Per-unit display price.
                                                RetailPrice already reflects the per-comprobante rule:
                                                  - A / NC A → NETO (sin IVA)
                                                  - B / C / Presupuesto / NC B / NC X → FINAL (IVA incluido)
                                                Divide by splitter to print the per-unit price (PRECIO Unitario). */}
                                            {formatPrice(roundedPrice / splitter)}
                                        </td>
                                        <td className="print-col-quantity">{quantity}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Display Sub Total, Discount, and Total Price */}
                    <div className="dash-separator"></div>
                    <div className="total-price-summary">
                        {/* FACTURA A: Show complete breakdown */}
                        {documentType === 'FACTURA A' && (
                            <>
                                {/* Show subtotal BEFORE discount if discount is applied */}
                                {discountPercentage > 0 && (
                                    <div className="summary-line">
                                        <span className="summary-label">Subtotal (sin IVA):</span>
                                        <span className="summary-amount">{formatPrice(subTotalPrice + discountAmount)}</span>
                                    </div>
                                )}
                                
                                {/* Show discount if applicable */}
                                {discountPercentage > 0 && (
                                    <div className="summary-line">
                                        <span className="summary-label">Descuento ({discountPercentage}%):</span>
                                        <span className="summary-amount">-{formatPrice(discountAmount)}</span>
                                    </div>
                                )}
                                
                                {/* Show subtotal after discount OR just subtotal if no discount */}
                                <div className="summary-line">
                                    <span className="summary-label">{discountPercentage > 0 ? 'Importe neto gravado:' : 'Subtotal (sin IVA):'}</span>
                                    <span className="summary-amount">{formatPrice(subTotalPrice)}</span>
                                </div>
                                
                                <div className="summary-line">
                                    <span className="summary-label">IVA (21%):</span>
                                    <span className="summary-amount">{formatPrice(totalAfterDiscount - subTotalPrice)}</span>
                                </div>
                            </>
                        )}
                        
                        {/* FACTURA B & C: Show single final price (Ley de Transparencia Fiscal)
                            Unit price in items grid already IVA-incluido (see mappedItems).
                            Show Subtotal (con IVA) → Discount (con IVA) → Total + leyenda informativa del IVA contenido.
                            Subtotal is computed as Total + Discount (so the arithmetic closes without rounding gaps). */}
                        {(documentType === 'FACTURA B' || documentType === 'FACTURA C') && (
                            <>
                                {discountPercentage > 0 && (() => {
                                    const discountConIva = getDiscountConIva(discountAmount);
                                    const subtotalConIva = totalAfterDiscount + discountConIva;
                                    return (
                                        <>
                                            <div className="summary-line">
                                                <span className="summary-label">Subtotal (IVA incluido):</span>
                                                <span className="summary-amount">{formatPrice(subtotalConIva)}</span>
                                            </div>
                                            <div className="summary-line">
                                                <span className="summary-label">Descuento ({discountPercentage}%):</span>
                                                <span className="summary-amount">-{formatPrice(discountConIva)}</span>
                                            </div>
                                        </>
                                    );
                                })()}

                                {discountPercentage === 0 && (
                                    <div className="summary-line">
                                        <span className="summary-label">Subtotal (IVA incluido):</span>
                                        <span className="summary-amount">{formatPrice(totalAfterDiscount)}</span>
                                    </div>
                                )}

                                {/* Informational leyenda: IVA contenido en el total (RG 1415 / RG 4540) */}
                                <div
                                    className="summary-line summary-iva-leyenda"
                                    style={{ fontStyle: 'italic', fontSize: '0.9em', color: '#555' }}
                                >
                                    <span className="summary-label">El precio incluye IVA 21%:</span>
                                    <span className="summary-amount">{formatPrice(calcIvaIncluido(totalAfterDiscount))}</span>
                                </div>
                            </>
                        )}
                        
                        {/* PRESUPUESTO: Show with IVA included */}
                        {documentType === 'PRESUPUESTO' && (
                            <>
                                {/* Show subtotal BEFORE discount if discount is applied */}
                                {discountPercentage > 0 && (
                                    <>
                                        <div className="summary-line">
                                            <span className="summary-label">Subtotal:</span>
                                            <span className="summary-amount">{formatPrice(totalAfterDiscount + (discountAmount * 1.21))}</span>
                                        </div>
                                        <div className="summary-line">
                                            <span className="summary-label">Descuento ({discountPercentage}%):</span>
                                            <span className="summary-amount">-{formatPrice(discountAmount * 1.21)}</span>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                        
                        {/* NOTA DE CREDITO A: Show IVA discrimination (AFIP required) */}
                        {(documentType === 'NOTA DE CREDITO A') && invoiceDetails && (
                            <>
                                <div className="summary-line">
                                    <span className="summary-label">Neto Gravado:</span>
                                    <span className="summary-amount">{formatPrice(invoiceDetails.subtotal || 0)}</span>
                                </div>
                                <div className="summary-line">
                                    <span className="summary-label">IVA 21%:</span>
                                    <span className="summary-amount">
                                        {formatPrice((invoiceDetails.total || 0) - (invoiceDetails.subtotal || 0))}
                                    </span>
                                </div>
                            </>
                        )}

                        {/* NOTA DE CREDITO B/X: No IVA discrimination */}
                        {(documentType === 'NOTA DE CREDITO B' || documentType === 'NOTA DE CREDITO X') && invoiceDetails && (
                            <div className="summary-line">
                                <span className="summary-label">Subtotal:</span>
                                <span className="summary-amount">{formatPrice(invoiceDetails.total || 0)}</span>
                            </div>
                        )}
                    </div>
                    <div className="total-separator"></div>
                    <div className="total-price-summary">
                        <div className="total-line">
                            <span>Total:</span>
                            <span>
                                {formatPrice(
                                    (documentType?.includes('NOTA DE CREDITO') && invoiceDetails?.total) 
                                        ? invoiceDetails.total 
                                        : totalAfterDiscount
                                )}
                            </span>
                        </div>
                    </div>
                    <div className="dash-separator"></div>
                    {/* Thank you message */}
                    <p className="thank-you-message">¡Gracias por su compra!</p>
            
                    {/* Payment Breakdown Section - Smart Otra Feature */}
                    {invoiceDetails?.paymentBreakdown?.splitPayment && (
                        <div className="payment-breakdown-section" style={{
                            marginTop: '16px',
                            paddingTop: '12px',
                            borderTop: '1px dashed #333'
                        }}>
                            <p style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '8px' }}>
                                FORMA DE PAGO (MIXTA):
                            </p>
                            {invoiceDetails.paymentBreakdown.methods?.map((method, idx) => (
                                <p key={idx} style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '11px',
                                    marginBottom: '4px'
                                }}>
                                    <span>• {method.name}:</span>
                                    <span>{formatPrice(method.amount)} ({method.percentage}%)</span>
                                </p>
                            ))}
                            <p style={{
                                marginTop: '8px',
                                fontSize: '10px',
                                fontStyle: 'italic',
                                color: '#666'
                            }}>
                                Código AFIP: 208 - Otra
                            </p>
                        </div>
                    )}
                </>
            )}
            
            {/* CAE Section - Only for Facturas - Moved to bottom */}
            {invoiceDetails && documentType.includes('FACTURA') && !documentType.includes('NOTA') && (
                <>
                    <div className="divider"></div>
                    <div className="cae-section-bottom">
                        <div className="cae-info-bottom">
                            {invoiceDetails.cae && (
                                <p><strong>CAE:</strong> {invoiceDetails.cae}</p>
                            )}
                            {invoiceDetails.caeVencimiento && (
                                <p><strong>Vencimiento CAE:</strong> {new Date(invoiceDetails.caeVencimiento).toLocaleDateString('es-AR')}</p>
                            )}
                        </div>
                        {invoiceDetails.afipQr && qrDataUrl && (
                            <div className="qr-code-container-bottom">
                                <img 
                                    src={qrDataUrl}
                                    alt="Código QR AFIP"
                                    className="qr-code"
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
            </div>
        </div>
        </>
    );
}

export default PrintQuotePage;